import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { Heart, Calendar, ArrowLeft, MessageCircle } from "lucide-react";

const DailyFeelingHistory = () => {
  const { language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [feelings, setFeelings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/daily-feeling/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeelings(response.data.feelings || []);
    } catch (error) {
      console.error("Error fetching feeling history:", error);
      toast.error(t("Error al cargar historial", "Error en carregar historial"));
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString(language === "ca" ? "ca-ES" : "es-ES", options);
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">
            {t("Cargando...", "Carregant...")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div data-testid="feeling-history-header" className="mb-8 fade-in">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t("Volver", "Tornar")}</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-pink-400 to-rose-600 flex items-center justify-center">
              <Heart className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold glow-text">
                {t("Historial de Sentimientos", "Historial de Sentiments")}
              </h1>
              <p className="text-gray-400 mt-1">
                {t("Tu camino emocional día a día", "El teu camí emocional dia a dia")}
              </p>
            </div>
          </div>
        </div>

        {/* Feelings List */}
        {feelings.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center border border-gray-700/30 fade-in">
            <Heart className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">
              {t(
                "Aún no tienes registros. Empieza compartiendo cómo te sientes hoy en el Dashboard.",
                "Encara no tens registres. Comença compartint com et sents avui al Dashboard."
              )}
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-4 btn-primary px-6 py-2"
            >
              {t("Ir al Dashboard", "Anar al Dashboard")}
            </button>
          </div>
        ) : (
          <div data-testid="feelings-list" className="space-y-6">
            {feelings.map((feeling, index) => (
              <div
                key={feeling.id || index}
                data-testid={`feeling-entry-${index}`}
                className="glass rounded-2xl p-6 border border-pink-400/10 hover:border-pink-400/20 transition-all fade-in"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Date Header */}
                <div className="flex items-center gap-2 mb-4 text-sm text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span className="capitalize">{formatDate(feeling.date)}</span>
                  {feeling.emotion_identified && (
                    <span className="ml-auto px-3 py-1 rounded-full bg-pink-500/20 text-pink-300 text-xs capitalize">
                      {feeling.emotion_identified}
                    </span>
                  )}
                </div>

                {/* User's Feeling */}
                <div className="mb-4">
                  <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">
                    {t("Lo que compartiste", "El que vas compartir")}
                  </h3>
                  <p className="text-gray-200 leading-relaxed bg-black/20 rounded-xl p-4">
                    {feeling.feeling}
                  </p>
                </div>

                {/* AI Response */}
                {feeling.ai_response && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <MessageCircle className="w-4 h-4 text-teal-400" />
                      <h3 className="text-xs uppercase tracking-wider text-gray-500">
                        {t("Reflexión de Eka", "Reflexió d'Eka")}
                      </h3>
                    </div>
                    <p className="text-teal-200/90 leading-relaxed italic border-l-2 border-teal-400/30 pl-4">
                      {feeling.ai_response}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Info */}
        {feelings.length > 0 && (
          <div className="mt-8 text-center text-sm text-gray-500 fade-in" style={{animationDelay: '0.3s'}}>
            <p>
              {t(
                `Mostrando los últimos ${feelings.length} registros`,
                `Mostrant els últims ${feelings.length} registres`
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyFeelingHistory;
